import {
  QueryObserverResult,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useGlobalStore } from '../../store/global-store/global-store';
import { createMilestone, deleteMilestone, fetchMilestones, updateMilestone } from './milestones-client';
import type { Milestone, MilestonePayload } from './milestones-client.type';

/**
 * Hook to fetch milestones for a given outcome
 *
 * @param {string} outcomeId - The ID of the outcome (mandatory)
 * @param {UseQueryOptions<Milestone[]>} options - Optional query options for configuring the query behavior
 * @param options - Optional query options for configuring the query behavior
 * @returns Object containing milestones array and query state
 */
function useMilestones(
  outcomeId: string | undefined,
  options?: UseQueryOptions<Milestone[]>,
): {
  milestones: Milestone[];
  query: QueryObserverResult<Milestone[]>;
} {
  const organizationId = useGlobalStore((state) => state.organization?.id);

  const query = useQuery<Milestone[]>({
    queryKey: ['milestones', organizationId, outcomeId],
    queryFn: () => fetchMilestones(organizationId ?? '', outcomeId ?? ''),
    enabled: !!organizationId && !!outcomeId,
    staleTime: 1000 * 10,
    gcTime: 1000 * 60 * 2,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    ...options,
  });

  return {
    milestones: query.data || [],
    query,
  };
}

/**
 * Hook to create a milestone
 *
 * @param {UseMutationOptions<Milestone, unknown, Partial<MilestonePayload>>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for creating a milestone
 */
function useCreateMilestoneMutation(
  options?: UseMutationOptions<Milestone, unknown, Partial<MilestonePayload>>,
): UseMutationResult<Milestone, unknown, Partial<MilestonePayload>> {
  const queryClient = useQueryClient();
  const organizationId = useGlobalStore((state) => state.organization?.id);

  return useMutation<Milestone, unknown, Partial<MilestonePayload>>({
    mutationFn: (milestone) => {
      if (!organizationId) {
        throw new Error('Organization ID is required to create a milestone');
      }
      return createMilestone({
        ...milestone,
        organization: organizationId,
      });
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['milestones'], exact: false });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Hook to update a milestone
 *
 * @param {UseMutationOptions<Milestone, unknown, { milestoneId: string; data: Partial<MilestonePayload> }>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for updating a milestone
 */
function useUpdateMilestoneMutation(
  options?: UseMutationOptions<Milestone, unknown, { milestoneId: string; data: Partial<MilestonePayload> }>,
): UseMutationResult<Milestone, unknown, { milestoneId: string; data: Partial<MilestonePayload> }> {
  const queryClient = useQueryClient();
  const organizationId = useGlobalStore((state) => state.organization?.id);

  return useMutation<Milestone, unknown, { milestoneId: string; data: Partial<MilestonePayload> }>({
    mutationFn: ({ milestoneId, data }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required to update a milestone');
      }
      return updateMilestone({
        ...data,
        id: milestoneId,
        organization: organizationId,
      });
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['milestones'], exact: false });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Hook to delete a milestone
 *
 * @param {UseMutationOptions<void, unknown, string>} options - Optional mutation options for configuring the mutation behavior
 * @returns Mutation object for deleting a milestone
 */
function useDeleteMilestoneMutation(
  options?: UseMutationOptions<void, unknown, string>,
): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationFn: deleteMilestone,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['milestones'], exact: false });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    ...options,
  });
}

export { useCreateMilestoneMutation, useDeleteMilestoneMutation, useMilestones, useUpdateMilestoneMutation };
